import { useCallback, useEffect } from 'react'

const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ''

/**
 * reCAPTCHA v3 hook — invisible, per-request token issuance.
 *
 * Loads Google's script tag once per page (idempotent — safe to
 * call from multiple mounted components) and exposes a
 * `getRecaptchaToken(action)` fn the caller invokes right before
 * a form POST. The returned token goes in the POST body as
 * `recaptcha_token`; the backend verifies it via
 * `utils.recaptcha.verify_recaptcha`.
 *
 * If `NEXT_PUBLIC_RECAPTCHA_SITE_KEY` is empty (dev builds without
 * a key) the hook returns an empty-string token; the backend's
 * `_recaptcha_gate` fails-open on that, so no reject.
 *
 * Extracted from contact.js:115-134 (2026-09-01) so signup /
 * forgot-password / verify-email pages share one implementation.
 */
export default function useRecaptchaV3() {
  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY) return
    if (document.querySelector('script[src*="recaptcha"]')) return
    const script = document.createElement('script')
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`
    script.async = true
    document.head.appendChild(script)
  }, [])

  const getRecaptchaToken = useCallback(async (action) => {
    if (!RECAPTCHA_SITE_KEY || !window.grecaptcha) return ''
    try {
      await new Promise(resolve => window.grecaptcha.ready(resolve))
      return await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action })
    } catch (e) {
      console.warn('reCAPTCHA failed:', e)
      return ''
    }
  }, [])

  return { getRecaptchaToken, isConfigured: !!RECAPTCHA_SITE_KEY }
}
